import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import LoadingSpinner from "./LoadingSpinner";
import { useAppConfig } from "../ConfigContext";
import createAdHandler from "monetag-tg-sdk";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

const MONETAG_REWARDED_ZONE_ID = 11758650;

const monetagRewardedAd =
  MONETAG_REWARDED_ZONE_ID
    ? createAdHandler(MONETAG_REWARDED_ZONE_ID)
    : null;

async function readError(res) {
  try {
    const text = await res.text();
    try {
      const j = JSON.parse(text);
      return j?.error || j?.message || text || `HTTP ${res.status}`;
    } catch {
      return text || `HTTP ${res.status}`;
    }
  } catch {
    return `HTTP ${res.status}`;
  }
}

function formatDhms(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (x) => String(x).padStart(2, "0");
  return `${pad(d)}d:${pad(h)}h:${pad(m)}m:${pad(s)}s`;
}

function weightedPickIndex(items) {
  const entries = items.map((it) => Math.max(0, Number(it.weight ?? 1)));
  const sum = entries.reduce((a, b) => a + b, 0);
  if (sum <= 0) return Math.floor(Math.random() * items.length);
  let r = Math.random() * sum;
  for (let i = 0; i < items.length; i++) {
    r -= entries[i];
    if (r <= 0) return i;
  }
  return items.length - 1;
}

function WinModal({ open, prize, onClose, onTryAgain, onDouble }) {
  const amount = prize?.label ?? "";
const sub = prize?.sub ?? "";
const icon = prize?.icon || "/runes.png";

const type = prize?.item?.type ?? "";

const isBalanceReward =
  String(type).toUpperCase().includes("RUNE") ||
  String(type).toUpperCase().includes("SPARK");

  return (
    <div>
      {open && (
        <div className="modal_box">
        <div className="modal_title">Hey<button onClick={onTryAgain ?? onClose} className="modal_close"/></div>
          <motion.div
           className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          >

          <div className="modal_inside">
           
                        <div
              
            >
              {/* Icon */}
              <div style={{ position: "relative", marginBottom: 8 }}>
                <img
                  src={icon}
                  alt=""
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "contain",
                    position: "relative",
                    filter: "drop-shadow(0px 10px 0px rgba(0,0,0,0.35))",
                  }}
                />
              </div>

              {/* Amount */}
              <div
              className="shadow2"
                style={{
                  fontSize: 28,
                  color: "#fff",
                  marginTop: -5,
                  fontWeight: 600
                }}
              >
                {amount}
              </div>

              <div
              className="shadow2"
                style={{
                  
                  fontSize: 28,
                  color: "#fff",
                  fontWeight: 600,
                  marginTop: 15,
                  marginBottom: 10,
                  letterSpacing: 1,
                }}
              >
                You Win!
              </div>

              {/* Sub label */}
              {sub && (
  <div
  className="info"
    style={{
      fontSize: 18,
    }}
  >
    {sub} {isBalanceReward ? "Added To Your Balance" : "Added To Your Inventory"}
  </div>
)}

</div> </div>
            <div className="modal_footer">
                {/* TRY AGAIN */}
                <button className="game-button green-button"
                  onClick={onTryAgain ?? onClose}
                >
                  Play Again
                </button>
              </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default function FortuneWheel({
  onClose,
  onNavigate,
  user,
  onSpinResult,
  setUser,
  freeCooldownMs = 24 * 60 * 60 * 1000,
  soundEnabled,
  sfxEnabled,
  sfxVolume,
}) {

  const goBackToArenas = useCallback(() => {
  if (typeof onNavigate === "function") {
    onNavigate("arenas");
    return;
  }
  onClose?.();
}, [onNavigate, onClose]);


  const {
    fortuneWheel: fw = { spinPrice: 0, cooldownSeconds: 86400, slots: [] },
    loaded: configLoaded,
  } = useAppConfig();

  const COOLDOWN_MS =
    Math.max(0, Number(fw?.cooldownSeconds ?? 86400)) * 1000;

  const audioCtxRef = useRef(null);
  const lastTickSliceRef = useRef(null);
  const lastTickTimeRef = useRef(0);

  const swipeRef = useRef({
    active: false,
    startedAt: 0,
    x0: 0,
    y0: 0,
    fired: false,
    pointerId: null,
  });

  const sfxGain = useMemo(() => {
    if (!sfxEnabled) return 0;
    return clamp(Number(sfxVolume ?? 1), 0, 1);
  }, [sfxEnabled, sfxVolume]);


  // Buy Extra Spins modals (Shop.js-style)
  const [showBuySpinModal, setShowBuySpinModal] = useState(false);
  const [showBuySpinSuccess, setShowBuySpinSuccess] = useState(false);
  const [showBuySpinError, setShowBuySpinError] = useState(false);
  const [buySpinErrorMessage, setBuySpinErrorMessage] = useState("");
  const [buySpinLoading, setBuySpinLoading] = useState(false);

  const playTick = useCallback((p = 0) => {
    if (!sfxEnabled || sfxGain <= 0) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const t = ctx.currentTime;

      // p: 0 -> start (fast), 1 -> end (slow)
      const slow = Math.min(1, Math.max(0, p));

      // Drop pitch as it slows: ~1600Hz down to ~700Hz
      const freq = 1600 - slow * 900;

      // Make it a bit louder + slightly longer near the end
      const vol = (0.18 + slow * 0.18) * sfxGain;
      const dur = 0.03 + slow * 0.03; // 30ms -> 60ms

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      // square is clicky; triangle is softer. square feels more “roulette”.
      osc.type = "square";
      osc.frequency.setValueAtTime(freq, t);

      gain.gain.setValueAtTime(vol, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + dur);

      // Optional: tiny low thump near the end for “heavier” feel
      let thump, thumpGain;
      if (slow > 0.78) {
        thump = ctx.createOscillator();
        thumpGain = ctx.createGain();
        thump.type = "sine";
        thump.frequency.setValueAtTime(120 - slow * 30, t); // ~120 -> ~90
        thumpGain.gain.setValueAtTime((0.10 * (slow - 0.78) / 0.22) * sfxGain, t);
        thumpGain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
        thump.connect(thumpGain);
        thumpGain.connect(ctx.destination);
      }

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(t);
      osc.stop(t + dur);

      if (thump) {
        thump.start(t);
        thump.stop(t + 0.08);
      }
    } catch {}
  }, [sfxEnabled, sfxGain]);

  const playWinChime = useCallback(() => {
    if (!sfxEnabled || sfxGain <= 0) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }

      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;

      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();

      osc1.type = "sine";
      osc2.type = "sine";

      // First note
      osc1.frequency.setValueAtTime(880, now); // A5
      gain1.gain.setValueAtTime(0.25 * sfxGain, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

      // Second note slightly higher
      osc2.frequency.setValueAtTime(1175, now + 0.15); // D6
      gain2.gain.setValueAtTime(0.25 * sfxGain, now + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

      osc1.connect(gain1);
      gain1.connect(ctx.destination);

      osc2.connect(gain2);
      gain2.connect(ctx.destination);

      osc1.start(now);
      osc1.stop(now + 0.25);

      osc2.start(now + 0.15);
      osc2.stop(now + 0.4);
    } catch {}
  }, [sfxEnabled, sfxGain]);

  const playFinalClack = useCallback(() => {
    if (!sfxEnabled || sfxGain <= 0) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const t = ctx.currentTime;

      // --- noise burst (click) ---
      const bufferSize = Math.floor(ctx.sampleRate * 0.06); // 60ms
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        // fast-decaying noise
        data[i] =
          (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 3);
      }

      const noise = ctx.createBufferSource();
      noise.buffer = buffer;

      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.setValueAtTime(900, t);

      const clickGain = ctx.createGain();
      clickGain.gain.setValueAtTime(0.45 * sfxGain, t);
      clickGain.gain.exponentialRampToValueAtTime(0.001, t + 0.06);

      noise.connect(hp);
      hp.connect(clickGain);
      clickGain.connect(ctx.destination);

      // --- low thump (mechanical hit) ---
      const thump = ctx.createOscillator();
      thump.type = "sine";
      thump.frequency.setValueAtTime(110, t);

      const thumpGain = ctx.createGain();
      thumpGain.gain.setValueAtTime(0.25 * sfxGain, t);
      thumpGain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);

      thump.connect(thumpGain);
      thumpGain.connect(ctx.destination);

      noise.start(t);
      noise.stop(t + 0.06);

      thump.start(t);
      thump.stop(t + 0.08);
    } catch {}
  }, [sfxEnabled, sfxGain]);

  const playCasinoWin = useCallback(() => {
    if (!sfxEnabled || sfxGain <= 0) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const t0 = ctx.currentTime;

      const notes = [659, 784, 988, 1319]; // E5, G5, B5, E6 (sparkly)
      notes.forEach((freq, i) => {
        const t = t0 + i * 0.06;

        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, t);

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.22 * sfxGain, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);

        // tiny “bell” feel
        const lp = ctx.createBiquadFilter();
        lp.type = "lowpass";
        lp.frequency.setValueAtTime(4200, t);

        osc.connect(lp);
        lp.connect(gain);
        gain.connect(ctx.destination);

        osc.start(t);
        osc.stop(t + 0.24);
      });
    } catch {}
  }, [sfxEnabled, sfxGain]);

  // Prize definitions (match your screenshot vibe: x2, 250, 500, 25, etc.)
  // You can swap icons to your own assets later.
  const prizes = useMemo(() => {
    const slots = Array.isArray(fw?.slots) ? fw.slots : [];

    const pickTheme = (type, fallback = "mint") => {
      const t = String(type || "").toUpperCase();
      if (t.includes("RUNE")) return "purple2";
      if (t.includes("SPARK")) return "blue3";
      if (t.includes("CARD")) return "green";
      if (t.includes("BOOST")) return "blue2";
      if (t.includes("CHEST") || t.includes("PACK")) return "brown";
      return fallback;
    };

    const normalizeType = (type) =>
      String(type || "").trim().toUpperCase().replace(/[\s-]+/g, "_");

    const pickIcon = (type) => {
      const t = normalizeType(type);

      if (t.includes("RUNE")) return "/runes.png";
      if (t.includes("SPARK")) return "/sparks.png";
      if (t.includes("CARD_PIECE")) return "/pieces.png";
      if (t.includes("BOOST")) return "/energy.png";
      if (t.includes("ABILITY")) return "/fightlog.png";
      if (t.includes("SEASONAL_ACCESSORY")) return "/fragment.png";
      if (t.includes("SPINS")) return "/fortuneicon.png";
      if (t.includes("CARD")) return "/pack.png";
      if (t.includes("CHEST") || t.includes("PACK")) return "/pack.png";
      if (t.includes("GAMEPASS") || t.includes("PACK")) return "/passicon.png";

      return "/runes.png";
    };

    const parseLabelSub = (slot) => {
      // Normalize possible backend field names
      const prizeItemId =
        slot?.prizeItemId ??
        slot?.prize_item_id ??
        slot?.itemId ??
        slot?.item_id ??
        null;

      const prizeGamepassItemId =
        slot?.prizeGamepassItemId ??
        slot?.prize_gamepass_item_id ??
        slot?.gamepassItemId ??
        slot?.gamepass_item_id ??
        null;

      const name = String(slot?.item?.name ?? slot?.name ?? "").trim();
      const type = slot?.item?.type ?? slot?.type ?? "";

      // If CMS provides label/sub/icon/theme, use them
      if (slot?.label != null || slot?.sub != null) {
        return {
          label: String(slot?.label ?? ""),
          sub: String(slot?.sub ?? ""),
          icon: String(slot?.icon ?? pickIcon(type)),
          theme: String(slot?.theme ?? pickTheme(type)),
          prizeItemId,
          prizeGamepassItemId,
        };
      }

      // Infer from name/type
      const x2 = /x\s*2/i.test(name);
      const num = name.match(/(\d+)/)?.[1] ?? "";

      // Only "Try again" if the slot is truly empty of prize info
      const hasAnyPrizeInfo =
        Boolean(prizeItemId || prizeGamepassItemId) ||
        Boolean(name) ||
        Boolean(type);

      if (!hasAnyPrizeInfo) {
        return {
          label: "10",
          sub: "",
          icon: "/usdt.png",
          theme: "mint",
          prizeItemId,
          prizeGamepassItemId,
        };
      }

      if (x2) {
        return {
          label: "x2",
          sub: name.replace(/x\s*2/i, "").trim() || "x2",
          icon: pickIcon(type),
          theme: pickTheme(type),
          prizeItemId,
          prizeGamepassItemId,
        };
      }

      if (num) {
        return {
          label: num,
          sub: name.replace(num, "").trim() || String(type || ""),
          icon: pickIcon(type),
          theme: pickTheme(type),
          prizeItemId,
          prizeGamepassItemId,
        };
      }

      return {
        label: "",
        sub: name || String(type || "Reward"),
        icon: pickIcon(type),
        theme: pickTheme(type),
        prizeItemId,
        prizeGamepassItemId,
      };
    };

    // Always render exactly 8 slices (NO fallback prizes, just empty slots become Try again)
    return Array.from({ length: 8 }).map((_, i) => {
      const slot = slots[i] || {};
      const w = Number(slot?.weight);

      const { label, sub, icon, theme, prizeItemId, prizeGamepassItemId } =
        parseLabelSub(slot);

      return {
        key: slot?.key ?? slot?.id ?? `slot_${i}`,
        label,
        sub,
        icon,
        theme,
        weight: Number.isFinite(w) && w >= 0 ? w : 1,
        prizeItemId,
        prizeGamepassItemId,
        item: slot?.item ?? null,
      };
    });
  }, [fw]);

  // Colors per “theme”
  const themeColors = useMemo(
    () => ({
      green: { a: "#7fd36a", b: "#3aa85c" },
      mint: { a: "#8fe8d1", b: "#4bb7a2" },
      blue: { a: "#6ad0ff", b: "#287bd9" },
      blue2: { a: "#4f84ff", b: "#2a3ed0" },
      blue3: { a: "#8bd8ff", b: "#3a7ad9" },
      purple: { a: "#b86cff", b: "#6f2dbd" },
      purple2: { a: "#ff7bd7", b: "#8b2aa7" },
      brown: { a: "#c17a3b", b: "#6b3b1b" },
    }),
    []
  );

  const wheelCanvasRef = useRef(null);
  const iconCacheRef = useRef(new Map());

  // Rotation is stored in degrees (visual)
  const [rotationDeg, setRotationDeg] = useState(0);
  const rotationRef = useRef(0);

  const [spinning, setSpinning] = useState(false);
  const [resultIdx, setResultIdx] = useState(null);
  const [resultOpen, setResultOpen] = useState(false);

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250); // smooth countdown
    return () => clearInterval(id);
  }, []);

  const [freeReady, setFreeReady] = useState(false);
  const [nextFreeAt, setNextFreeAt] = useState(null); // ISO string or null
  const [paidSpins, setPaidSpins] = useState(0);
  const [paidPriceRunes, setPaidPriceRunes] = useState(0);
  const [fortuneStateLoading, setFortuneStateLoading] = useState(true);

  // Monetag Rewarded Interstitial
const [rewardedAd, setRewardedAd] = useState({
  enabled: false,
  dailyLimit: 0,
  usedToday: 0,
  remainingToday: 0,
  canWatch: false,
  rewardType: "SPIN",
  rewardAmount: 1,
});

const [rewardedAdLoading, setRewardedAdLoading] = useState(false);
const [rewardedAdMessage, setRewardedAdMessage] = useState("");

  // derived for countdown
  const nextFreeAtMs = useMemo(() => {
    if (!nextFreeAt) return 0;
    const t = new Date(nextFreeAt).getTime();
    return Number.isFinite(t) ? t : 0;
  }, [nextFreeAt]);

  // total tries shown in UI: 1 if freeReady, otherwise 0
  const totalSpins = paidSpins;
  const claimReady = nextFreeAtMs <= now;

  const drawWheel = useCallback(() => {
    const canvas = wheelCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const cssSize = 300; // wheel visual size
    canvas.width = Math.floor(cssSize * dpr);
    canvas.height = Math.floor(cssSize * dpr);
    canvas.style.width = `${cssSize}px`;
    canvas.style.height = `${cssSize}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cx = cssSize / 2;
    const cy = cssSize / 2;
    const r = cssSize / 2 - 8;

    ctx.clearRect(0, 0, cssSize, cssSize);

    // Outer ring
    ctx.beginPath();
    ctx.arc(cx, cy, r + 6, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.fill();

    ctx.beginPath();
    ctx.arc(cx, cy, r + 2, 0, Math.PI * 2);
    ctx.fillStyle = "#d3a84b";
    ctx.fill();

    // Wheel slices
    const N = prizes.length;
    const slice = (Math.PI * 2) / N;

    for (let i = 0; i < N; i++) {
      const p = prizes[i];
      const themeKeys = Object.keys(themeColors);
      const forcedTheme = themeKeys[i % themeKeys.length];
      const colors = themeColors[forcedTheme];

      const a0 = i * slice;
      const a1 = a0 + slice;

      // slice gradient
      const gx = cx + Math.cos((a0 + a1) / 2) * (r * 0.2);
      const gy = cy + Math.sin((a0 + a1) / 2) * (r * 0.2);
      const grad = ctx.createRadialGradient(gx, gy, 2, cx, cy, r);
      grad.addColorStop(0, colors.a);
      grad.addColorStop(1, colors.b);

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, a0, a1);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();

      // divider
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a0) * r, cy + Math.sin(a0) * r);
      ctx.strokeStyle = "rgba(0,0,0,0.35)";
      ctx.lineWidth = 2;
      ctx.stroke();

      const mid = (a0 + a1) / 2;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(mid);
      ctx.translate(r * 0.75, 0);
      ctx.rotate(Math.PI / 2);

      // --- ICON (top) ---
      if (p.icon) {
        const cached = iconCacheRef.current.get(p.icon);
        if (cached?.complete && cached?.naturalWidth > 0) {
          const iw = 32;
          const ih = 32;
          ctx.drawImage(cached, -iw / 2, -4, iw, ih);
        }
      }

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      // text size
      ctx.font = "900 14px system-ui";
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.strokeStyle = "rgba(0,0,0,0.9)";
      ctx.lineWidth = 2;

      ctx.strokeText(String(p.label), 0, 48);
      ctx.fillText(String(p.label), 0, 48);

      ctx.restore();
    }

    // Center hub
    ctx.beginPath();
    ctx.arc(cx, cy, 34, 0, Math.PI * 2);
    ctx.fillStyle = "#f6d36a";
    ctx.fill();

    ctx.beginPath();
    ctx.arc(cx, cy, 26, 0, Math.PI * 2);
    ctx.fillStyle = "#c96a25";
    ctx.fill();

    ctx.beginPath();
    ctx.arc(cx, cy, 9, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.fill();
  }, [prizes, themeColors]);

  // preload icons + draw
  useEffect(() => {
    for (const p of prizes) {
      if (!p.icon) continue;
      if (iconCacheRef.current.has(p.icon)) continue;
      const im = new Image();
      im.src = p.icon;
      iconCacheRef.current.set(p.icon, im);
      im.onload = () => drawWheel();
    }
    drawWheel();
  }, [prizes, drawWheel]);

  // apply rotation to wrapper (canvas itself stays static; wrapper rotates)
  useEffect(() => {
    rotationRef.current = rotationDeg;
  }, [rotationDeg]);

  const spinToIndex = useCallback(
    async (idx, { force = false, onFinish } = {}) => {
      if (spinning) return;
      if (!force && paidSpins <= 0) return;

      setSpinning(true);
      setResultIdx(null);

      const N = prizes.length;
      const sliceDeg = 360 / N;

      // pointer is at top; we want chosen slice center to land at -90deg (top)
      const pointerDeg = -90;

      const chosenCenterDeg = idx * sliceDeg + sliceDeg / 2;

      // We rotate wheel so that chosenCenter aligns with pointer.
      // We add many full spins for drama:
      const extraSpins = 6 + Math.floor(Math.random() * 3); // 6–8
      const target = extraSpins * 360 + (pointerDeg - chosenCenterDeg);

      const start = rotationRef.current;
      const delta = target - (start % 360);

      const durationMs = 3600 + Math.floor(Math.random() * 400);

      const t0 = performance.now();
      const animate = (t) => {
        const p = clamp((t - t0) / durationMs, 0, 1);
        const eased = easeOutCubic(p);

        // tiny “wobble” near the end
        const wobble =
          p > 0.88 ? Math.sin((p - 0.88) * 40) * (1 - p) * 8 : 0;

        const v = start + delta * eased + wobble;
        setRotationDeg(v);

        const sliceDeg = 360 / prizes.length;

        const normalized = ((v % 360) + 360) % 360;
        const pointerShifted = (normalized + 90) % 360;

        const sliceIndexUnderPointer = Math.floor(pointerShifted / sliceDeg);

        const now = performance.now();
        if (
          sliceIndexUnderPointer !== lastTickSliceRef.current &&
          now - lastTickTimeRef.current > 35
        ) {
          lastTickSliceRef.current = sliceIndexUnderPointer;
          lastTickTimeRef.current = now;
          playTick(p);
        }

        if (p < 1) {
          requestAnimationFrame(animate);
        } else {
          // settle
          setRotationDeg(start + delta);
          setSpinning(false);
          setResultIdx(idx);
          playFinalClack();
          setTimeout(() => playCasinoWin(), 80);
          setResultOpen(true);

          const prize = prizes[idx];
          onSpinResult?.(prize);
          onFinish?.();
        }
      };

      requestAnimationFrame(animate);
    },
    [
      spinning,
      paidSpins,
      prizes,
      onSpinResult,
      playTick,
      playFinalClack,
      playCasinoWin,
    ]
  );

  const fetchFortuneState = useCallback(async () => {
  const token = localStorage.getItem("token");

  try {

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/fortune-wheel/state`,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    if (!res.ok) {
      throw new Error(await readError(res));
    }

    const data = await res.json().catch(() => ({}));

    setFreeReady(Boolean(data?.freeReady));
    setNextFreeAt(data?.nextFreeAt ?? null);

    setPaidSpins(
      Number(
        data?.totalSpins ??
          data?.spinsAvailable ??
          data?.paidSpins ??
          0
      )
    );

    setPaidPriceRunes(Number(data?.paidPrice ?? 0));

    if (data?.rewardedAd) {
  setRewardedAd({
    enabled:
      Boolean(data.rewardedAd.enabled),

    dailyLimit:
      Number(data.rewardedAd.dailyLimit || 0),

    usedToday:
      Number(data.rewardedAd.usedToday || 0),

    remainingToday:
      Number(data.rewardedAd.remainingToday || 0),

    canWatch:
      Boolean(data.rewardedAd.canWatch),

    rewardType:
      String(
        data.rewardedAd.rewardType || "SPIN"
      ).toUpperCase(),

    rewardAmount:
      Number(data.rewardedAd.rewardAmount || 1),
  });
}

    const newRunes =
      data?.runes ??
      data?.user?.runes ??
      data?.me?.runes ??
      null;

    const newSparks =
      data?.sparks ??
      data?.user?.sparks ??
      data?.me?.sparks ??
      null;

    if (
      typeof setUser === "function" &&
      (newRunes != null || newSparks != null)
    ) {
      setUser((prev) => ({
        ...(prev || {}),
        ...(newRunes != null
          ? { runes: Number(newRunes) }
          : {}),
        ...(newSparks != null
          ? { sparks: Number(newSparks) }
          : {}),
      }));
    }

    return data;
  } finally {
    setFortuneStateLoading(false);
  }
}, [setUser]);

  useEffect(() => {
    fetchFortuneState().catch(console.error);
  }, [fetchFortuneState]);

  const confirmBuyExtraSpin = useCallback(async () => {
    if (buySpinLoading) return;

    setBuySpinLoading(true);
    setBuySpinErrorMessage("");
    setShowBuySpinError(false);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/fortune-wheel/buy-extra-spin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ qty: 1 }),
        }
      );

      const text = await res.text();
      let data = {};
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }

      if (!res.ok) {
        throw new Error(
          data?.error || data?.message || text || `HTTP ${res.status}`
        );
      }

      const newRunes =
  data?.runes ?? data?.user?.runes ?? data?.fortune?.runes ?? null;

const newSparks =
  data?.sparks ?? data?.user?.sparks ?? data?.fortune?.sparks ?? null;

if (typeof setUser === "function" && (newRunes != null || newSparks != null)) {
  setUser((prev) => ({
    ...(prev || {}),
    ...(newRunes != null ? { runes: Number(newRunes) } : {}),
    ...(newSparks != null ? { sparks: Number(newSparks) } : {}),
  }));
}

      // ✅ still refresh wheel truth (paid spins, freeReady, paid price etc)
      await fetchFortuneState();

      setShowBuySpinModal(false);
      setShowBuySpinSuccess(true);
    } catch (e) {
      setShowBuySpinModal(false);
      setBuySpinErrorMessage(e?.message || "Transaction Failed");
      setShowBuySpinError(true);
    } finally {
      setBuySpinLoading(false);
    }
  }, [buySpinLoading, fetchFortuneState, setUser]);

  const spinServer = useCallback(async () => {
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/fortune-wheel/spin`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.error || "Spin failed");
    }

    // update state from backend truth
    setNextFreeAt(data?.nextFreeAt ?? null);

    // runes changed on spin
    //if (typeof setUser === "function" && typeof data?.chargedRunes === "number") {
    //  setUser((prev) => {
    //    const prevRunes = Number(prev?.runes ?? 0);
    //    return { ...(prev || {}), runes: Math.max(0, prevRunes - data.chargedRunes) };
    //  });
    //}

    //fetchFortuneState().catch(() => {});
    return data;
  }, [setUser, fetchFortuneState]);

  const claimServer = useCallback(async () => {
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/fortune-wheel/claim-free-spin`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.error || "Claim failed");
    }

    // backend truth
    setNextFreeAt(data?.nextFreeAt ?? null);
    setFreeReady(false);
    setPaidSpins(
      Number(data?.totalSpins ?? data?.spinsAvailable ?? data?.paidSpins ?? 0)
    );

    // re-pull full state (prices, slots etc.)
    fetchFortuneState().catch(() => {});

    return data;
  }, [fetchFortuneState]);

  const applyPrizeBalanceToUser = useCallback((prize) => {
  if (typeof setUser !== "function" || !prize) return;

  const type = String(prize?.item?.type ?? "").toUpperCase();
  const amount = Number(prize?.label ?? 0);

  if (!Number.isFinite(amount) || amount <= 0) return;

  setUser((prev) => {
    const next = { ...(prev || {}) };

    if (type.includes("SPARK")) {
      next.sparks = Number(next.sparks ?? 0) + amount;
    }

    return next;
  });
}, [setUser]);

  const handleSpinButton = useCallback(async () => {
  if (spinning) return;

  if (paidSpins <= 0) {
    setShowBuySpinSuccess(false);
    setShowBuySpinError(false);
    setBuySpinErrorMessage("");
    setShowBuySpinModal(true);
    return;
  }

  try {
    const data = await spinServer();
    const winningPrize = prizes[Number(data.resultIndex)];

    await spinToIndex(Number(data.resultIndex), {
      force: true,
      onFinish: async () => {
        applyPrizeBalanceToUser(winningPrize);
        await fetchFortuneState();
      },
    });
  } catch (e) {
    console.error(e);
    fetchFortuneState().catch(() => {});
  }
}, [
  spinning,
  paidSpins,
  spinServer,
  spinToIndex,
  fetchFortuneState,
  prizes,
  applyPrizeBalanceToUser,
]);
  
    const trySwipeSpin = useCallback(() => {
    if (spinning) return;
    if (resultOpen) return;
    if (showBuySpinModal || showBuySpinError || showBuySpinSuccess) return;
    handleSpinButton();
  }, [
    spinning,
    resultOpen,
    showBuySpinModal,
    showBuySpinError,
    showBuySpinSuccess,
    handleSpinButton,
  ]);

  const onWheelPointerDown = useCallback(
    (e) => {
      if (spinning || resultOpen) return;
      if (e.button != null && e.button !== 0) return; // ignore right-click

      swipeRef.current.active = true;
      swipeRef.current.startedAt = performance.now();
      swipeRef.current.x0 = e.clientX;
      swipeRef.current.y0 = e.clientY;
      swipeRef.current.fired = false;
      swipeRef.current.pointerId = e.pointerId;

      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [spinning, resultOpen]
  );

  const onWheelPointerMove = useCallback(
    (e) => {
      const s = swipeRef.current;
      if (!s.active || s.fired) return;

      const dt = performance.now() - s.startedAt;
      const dx = e.clientX - s.x0;
      const dy = e.clientY - s.y0;

      const dist = Math.hypot(dx, dy);

      // Tune these if needed
      const MIN_DIST = 70;  // px
      const MAX_TIME = 350; // ms

      if (dist >= MIN_DIST && dt <= MAX_TIME) {
        s.fired = true;
        trySwipeSpin();
      }
    },
    [trySwipeSpin]
  );

  const endSwipe = useCallback(() => {
    const s = swipeRef.current;
    if (!s.active) return;
    s.active = false;
    s.pointerId = null;
  }, []);

  const onWheelPointerUp = endSwipe;
  const onWheelPointerCancel = endSwipe;


  const handleBuyExtraSpins = useCallback(() => {
    if (spinning) return;

    // reset states each time
    setShowBuySpinSuccess(false);
    setShowBuySpinError(false);
    setBuySpinErrorMessage("");

    setShowBuySpinModal(true);
  }, [spinning]);

  const handleClaimFreeSpin = useCallback(async () => {
    if (spinning) return;
    if (!claimReady) return;

    try {
      await claimServer();
      await fetchFortuneState();
    } catch (e) {
      console.error(e);
      fetchFortuneState().catch(() => {});
    }
  }, [spinning, claimReady, claimServer, fetchFortuneState]);

  
  const showRewardedButtonMessage = useCallback((message, ms = 1800) => {
  setRewardedAdMessage(message);

  setTimeout(() => {
    setRewardedAdMessage("");
  }, ms);
}, []);

const handleRewardedAd = useCallback(async () => {
  if (rewardedAdLoading) return;
  if (spinning) return;

  if (!rewardedAd?.canWatch) {
    showRewardedButtonMessage(
      "DAILY LIMIT REACHED",
      2000
    );
    return;
  }

  setRewardedAdLoading(true);
  setRewardedAdMessage("");

  try {
    const token = localStorage.getItem("token");

    // --------------------------------------------------
    // 1. PREPARE REWARDED AD
    // --------------------------------------------------

    const prepareRes = await fetch(
      `${process.env.REACT_APP_API_URL}/api/fortune-wheel/rewarded-ad/prepare`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token
            ? { Authorization: `Bearer ${token}` }
            : {}),
        },
        body: JSON.stringify({}),
      }
    );

    const prepareText = await prepareRes.text();

    let prepareData = {};

    try {
      prepareData = prepareText
        ? JSON.parse(prepareText)
        : {};
    } catch {
      prepareData = {
        raw: prepareText,
      };
    }


    if (!prepareRes.ok) {

      if (
        prepareData?.error ===
        "REWARDED_AD_DAILY_LIMIT"
      ) {

        showRewardedButtonMessage(
          "DAILY LIMIT REACHED",
          2000
        );

        await fetchFortuneState();

        return;
      }


      throw new Error(
        prepareData?.error ||
        prepareData?.message ||
        prepareText ||
        `HTTP ${prepareRes.status}`
      );
    }


    const ymid = String(
      prepareData?.ymid || ""
    ).trim();


    if (!ymid) {
      throw new Error(
        "Backend did not return ymid"
      );
    }


    // --------------------------------------------------
    // 2. SHOW MONETAG REWARDED INTERSTITIAL
    // --------------------------------------------------

    if (typeof monetagRewardedAd !== "function") {
      throw new Error(
        "Monetag rewarded ad is not available"
      );
    }


    const adPromise = monetagRewardedAd({
      ymid,
      requestVar: "fortune_wheel_reward",
    });


    // IMPORTANT:
    //
    // Do NOT decide anything here based on
    // impression_valued.
    //
    // The backend may receive:
    //
    // impression + valued
    //
    // and only AFTER THAT:
    //
    // click + non_valued
    //
    // The click is what finally rewards the spin.
    //
    // This timeout only prevents the SDK promise
    // from blocking forever.

    await Promise.race([
  Promise.resolve(adPromise).catch(() => null),

  new Promise((resolve) =>
    setTimeout(resolve, 1500)
  ),
]);


    // --------------------------------------------------
    // 3. WAIT FOR SERVER-SIDE POSTBACK
    // --------------------------------------------------

let rewarded = false;
let rewardData = null;
let lastStatus = null;


    for (
      let attempt = 0;
      attempt < 10;
      attempt++
    ) {

      const statusRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/fortune-wheel/rewarded-ad/status/${encodeURIComponent(
          ymid
        )}`,
        {
          headers: {
            ...(token
              ? { Authorization: `Bearer ${token}` }
              : {}),
          },
        }
      );


      const statusData =
        await statusRes
          .json()
          .catch(() => ({}));

          if (statusRes.ok) {
  lastStatus = statusData?.status || null;
}


      // -----------------------------------------------
      // ONLY rewarded is final.
      //
      // DO NOT return on impression_valued.
      // -----------------------------------------------

      if (
        statusRes.ok &&
        statusData?.rewarded === true
      ) {

        rewarded = true;
        rewardData = statusData;

        break;
      }


      await new Promise((resolve) =>
        setTimeout(resolve, 1500)
      );
    }


    // --------------------------------------------------
    // 4. REWARD CONFIRMED
    // --------------------------------------------------

    if (rewarded) {

      const rewardType = String(
        rewardData?.rewardType ||
        rewardedAd.rewardType ||
        "SPIN"
      ).toUpperCase();


      const rewardAmount = Number(
        rewardData?.rewardAmount ||
        rewardedAd.rewardAmount ||
        1
      );


      if (rewardType === "SPIN") {

        showRewardedButtonMessage(
          `+${rewardAmount} SPIN${
            rewardAmount === 1 ? "" : "S"
          } ADDED!`
        );


      } else if (rewardType === "RUNES") {

        showRewardedButtonMessage(
          `+${rewardAmount.toLocaleString(
            "en-US"
          )} RUNES ADDED!`
        );


      } else if (rewardType === "SPARKS") {

        showRewardedButtonMessage(
          `+${rewardAmount.toLocaleString(
            "en-US"
          )} SPARKS ADDED!`
        );
      }


      await fetchFortuneState();

    } else {

  if (lastStatus === "impression_valued") {

    showRewardedButtonMessage(
      "AD CLOSED — NO REWARD",
      2200
    );

  } else {

    showRewardedButtonMessage(
      "REWARD NOT CONFIRMED",
      2200
    );
  }

  fetchFortuneState().catch(() => {});
}


  } catch (e) {

    console.error(
      "[Monetag Rewarded Ad]",
      e
    );


    showRewardedButtonMessage(
      e?.message || "AD FAILED",
      2000
    );


  } finally {

    setRewardedAdLoading(false);

  }

}, [
  rewardedAdLoading,
  spinning,
  rewardedAd,
  fetchFortuneState,
  showRewardedButtonMessage,
]);

const resultPrize = resultIdx != null ? prizes[resultIdx] : null;

return (
  <div
    className="shop-container"
    style={{
      overflowX: "hidden",
      position: "relative",
    }}
  >

    {fortuneStateLoading && (
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <LoadingSpinner
          text="Loading Fortune Wheel"
          size={64}
        />
      </div>
    )}

    <div
      style={{
        width: "100%",
        visibility: fortuneStateLoading ? "hidden" : "visible",
      }}
    >

      {/* Top bar (match screenshot layout conceptually) */}
      <div
        style={{
          marginTop: -10,
          marginLeft: -10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "calc(100% + 20px)",
          height: 80,
          padding: 10,
          boxSizing: "border-box",
          backgroundImage: "url('/fortune.png')",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          fontFamily: '"Fredoka", sans-serif',
        }}
      >
        {/* Subtitle */}
        <div
        className="shadow1"
          style={{
            fontSize: 18,
            color: "#ffd86b",
            fontWeight: 600,
            letterSpacing: 1,
            marginBottom: -6,
          }}
        >
          Every Day Test Your Luck
        </div>

        {/* Title */}
        <div
        className="shadow2"
          style={{
            fontWeight: 600,
            fontSize: 28,
            color: "#ffffff",
            marginTop: 10,
          }}
        >
          Fortune Wheel
        </div>
      </div>

      {/* Fortune Wheel navigation buttons */}
<div
  style={{
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr 1.3fr",
    gap: 8,
    marginTop: 12,
    marginBottom: 18,
    boxSizing: "border-box",
    fontFamily: '"Fredoka", sans-serif',
  }}
>
  {/* BUY SPINS */}
  <button
    type="button"
    onClick={handleBuyExtraSpins}
    disabled={spinning}
    style={{
      width: "100%",
      height: 54,
      minWidth: 0,
      margin: 0,
      padding: "0 6px",
      boxSizing: "border-box",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      background: "rgba(95, 49, 18, 0.76)",
      border: "2px solid rgba(70, 32, 10, 0.95)",
      borderRadius: 8,

      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 3px 0 rgba(0,0,0,0.24)",

      color: "#fff",
      fontSize: 16,
      lineHeight: "20px",
      fontWeight: 600,
      textAlign: "center",
      textTransform: "uppercase",
      whiteSpace: "nowrap",

      textShadow:
        "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",

      cursor: spinning ? "not-allowed" : "pointer",
      opacity: spinning ? 0.6 : 1,
      appearance: "none",
      WebkitAppearance: "none",
    }}
  >
    BUY SPINS
  </button>

  {/* AVAILABLE SPINS */}
  <div
    style={{
      width: "100%",
      height: 54,
      minWidth: 0,
      padding: "4px 5px",
      boxSizing: "border-box",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,

      background: "rgba(95, 49, 18, 0.76)",
      border: "2px solid rgba(70, 32, 10, 0.95)",
      borderRadius: 8,

      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 3px 0 rgba(0,0,0,0.24)",

      overflow: "hidden",
    }}
  >
    <img
      src="/fortuneicon.png"
      alt=""
      draggable={false}
      style={{
        width: 29,
        height: 29,
        objectFit: "contain",
        flexShrink: 0,
        filter: "drop-shadow(0 2px 2px rgba(0,0,0,.7))",
      }}
    />

    <div
      style={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          color: "#ffd35c",
          fontSize: 18,
          lineHeight: "20px",
          fontWeight: 600,
          whiteSpace: "nowrap",
          textShadow:
            "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
        }}
      >
       {Number(totalSpins || 0).toLocaleString("en-US")}
      </div>

      <div
        style={{
          color: "#fff",
          fontSize: 9,
          lineHeight: "13px",
          fontWeight: 600,
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          textShadow:
            "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
        }}
      >
        SPINS
      </div>
    </div>
  </div>

  {/* FREE SPIN */}
  <div
    style={{
      width: "100%",
      height: 54,
      minWidth: 0,
      padding: "3px 4px",
      boxSizing: "border-box",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 3,

      background: claimReady
        ? "rgba(112, 61, 27, 0.96)"
        : "rgba(95, 49, 18, 0.76)",

      border: claimReady
        ? "2px solid rgba(135, 78, 36, 0.98)"
        : "2px solid rgba(70, 32, 10, 0.95)",

      borderRadius: 8,

      boxShadow: claimReady
        ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.24), 0 3px 0 rgba(0,0,0,0.24)"
        : "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 3px 0 rgba(0,0,0,0.24)",

      overflow: "hidden",
    }}
  >
    <img
      src="/v2/clock2.png"
      alt=""
      draggable={false}
      style={{
        width: 25,
        height: 25,
        objectFit: "contain",
        flexShrink: 0,
        filter: "drop-shadow(0 2px 2px rgba(0,0,0,.7))",
      }}
    />

    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          color: "#ffd35c",
          fontSize: 9,
          lineHeight: "13px",
          fontWeight: 600,
          textAlign: "center",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textShadow:
            "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
        }}
      >
        {claimReady ? "FREE SPIN READY" : "NEXT FREE SPIN"}
      </div>

      {claimReady ? (
        <button
          type="button"
          className="game-button-small green-button"
          onClick={handleClaimFreeSpin}
          disabled={spinning}
          style={{
            width: "100%",
            maxWidth: 92,
            height: 25,
            minHeight: 25,
            margin: 0,
            padding: "0 4px",
            borderRadius: 6,
            fontSize: 11,
            lineHeight: "25px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            cursor: spinning ? "not-allowed" : "pointer",
            opacity: spinning ? 0.65 : 1,
          }}
        >
          CLAIM NOW
        </button>
      ) : (
        <div
          style={{
            color: "#fff",
            fontSize: 10,
            lineHeight: "18px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            textShadow:
              "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
          }}
        >
          {nextFreeAtMs > now
            ? formatDhms(nextFreeAtMs - now)
            : "READY!"}
        </div>
      )}
    </div>
  </div>
</div>
{rewardedAd.enabled && (
  <div
    style={{
      width: "100%",
      marginTop: -8,
      marginBottom: 18,
      fontFamily: '"Fredoka", sans-serif',
    }}
  >
    <button
      type="button"
      className="game-button green-button"
      onClick={handleRewardedAd}
      disabled={
        spinning ||
        rewardedAdLoading ||
        !rewardedAd.canWatch
      }
      style={{
        width: "100%",
        minHeight: 52,
        opacity: rewardedAd.canWatch ? 1 : 0.55,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        paddingTop: 5,
        paddingBottom: 5,
      }}
    >

      {rewardedAdMessage ? (
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            lineHeight: "18px",
          }}
        >
          {rewardedAdMessage}
        </div>
      ) : rewardedAdLoading ? (
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          LOADING AD...
        </div>
      ) : rewardedAd.canWatch ? (
        <>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              lineHeight: "18px",
            }}
          >
            WATCH AD — EARN{" "}

            {rewardedAd.rewardType === "SPIN"
              ? `${rewardedAd.rewardAmount} EXTRA SPIN${
                  rewardedAd.rewardAmount === 1 ? "" : "S"
                }`

              : rewardedAd.rewardType === "RUNES"
                ? `${Number(
                    rewardedAd.rewardAmount
                  ).toLocaleString("en-US")} RUNES`

                : `${Number(
                    rewardedAd.rewardAmount
                  ).toLocaleString("en-US")} SPARKS`
            }
          </div>

          <div
            style={{
              fontSize: 10,
              lineHeight: "12px",
              opacity: 0.9,
              fontWeight: 500,
            }}
          >
            {rewardedAd.remainingToday} OF{" "}
            {rewardedAd.dailyLimit} ADS LEFT TODAY
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              lineHeight: "18px",
            }}
          >
            DAILY AD LIMIT REACHED
          </div>

          <div
            style={{
              fontSize: 10,
              lineHeight: "12px",
              opacity: 0.9,
            }}
          >
            {rewardedAd.dailyLimit} ADS PER DAY
          </div>
        </>
      )}

    </button>
  </div>
)}

      {/* Wheel stage */}
      <div style={{ marginTop: 18, display: "grid", placeItems: "center" }}>
        <div
  style={{
    position: "relative",
    width: 300,
    height: 300,
    touchAction: "none", // IMPORTANT for mobile swipe
  }}
  onPointerDown={onWheelPointerDown}
  onPointerMove={onWheelPointerMove}
  onPointerUp={onWheelPointerUp}
  onPointerCancel={onWheelPointerCancel}
>

          {/* Pointer */}
          <img
            src="/wheel_arrow.png"
            alt=""
            style={{
              position: "absolute",
              top: -2, // adjust if needed
              left: "50%",
              transform: "translateX(-50%)",
              width: 50, // adjust size to match your design
              height: "auto",
              zIndex: 10,
              pointerEvents: "none",
              filter: "drop-shadow(0px 4px 0px rgba(0,0,0,0.5))",
            }}
          />

          {/* Center spotlight (small opaque circle) */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 150, // <-- tweak this size
              height: 150,
              borderRadius: "50%",
              pointerEvents: "none",
              zIndex: 5,
              background: "rgba(230, 170, 60, 0.25)",
            }}
          />

          {/* Glow ring while spinning */}
          <motion.div
            animate={{
              opacity: spinning ? 1 : 0.35,
              filter: spinning ? "blur(0px)" : "blur(1px)",
              scale: spinning ? 1.02 : 1,
            }}
            transition={{ duration: 0.25 }}
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 999,
              background:
                "radial-gradient(circle at 50% 45%, rgba(255,223,95,0.55), rgba(255,120,0,0.15), rgba(0,0,0,0) 60%)",
              mixBlendMode: "screen",
              pointerEvents: "none",
              zIndex: 2,
            }}
          />

          <img
            src="/wheel_center.png"
            alt=""
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 48, // adjust to your asset
              height: "auto",
              zIndex: 7, // above wheel, below arrow if arrow is 10
              pointerEvents: "none",
            }}
          />

          {/* Spinning canvas */}
          <motion.div
            animate={{ rotate: rotationDeg }}
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              zIndex: 3,
            }}
          >
            <canvas ref={wheelCanvasRef} />
          </motion.div>

          {/* Wheel frame overlay (ring-only using mask) */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 6,
              pointerEvents: "none",
              backgroundImage: "url(/wheel.png)",
              backgroundSize: "300px 300px",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              // The magic: show only outer ring area
              WebkitMaskImage:
                "radial-gradient(circle, transparent 58%, black 60%)",
              maskImage: "radial-gradient(circle, transparent 58%, black 60%)",
            }}
          />
        </div>
      </div>

      <div
        style={{
          marginTop: 18,
          width: "100%",
        }}
      >
        <motion.button
        style={{width: 150,display: "inline-block"}}
          type="button"
          className="game-button green-button"
          onClick={handleSpinButton}
          disabled={spinning}
          whileTap={{ scale: spinning ? 1 : 0.96 }}
        >
          SPIN
        </motion.button>
      </div>

      {showBuySpinModal && !showBuySpinError && !showBuySpinSuccess && (
        <div className="modal_box">

        <div className="modal_title">Confirm<button onClick={() => setShowBuySpinModal(false)} className="modal_close"/></div>
          <motion.div
            className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          >
          <div className="modal_inside">
            <img
              src="/finger.png"
              alt="Confirm"
              style={{ height: "70px", marginBottom: "10px" }}
            />

            <div className="shadow2" style={{ fontWeight: 600, fontSize: 28, marginBottom: 10}}>
              Confirm Purchase?
            </div>

            <p className="info shadow1"
              style={{
                fontSize: "21px",
                color: "#ddd",
              }}
            >
              Buy <span style={{ color: "#09BDFF" }}>1 Extra Spin</span> for{" "}
              <span style={{ color: "#ffbb01" }}>{paidPriceRunes} Runes</span>
            </p>

            </div>

            <div className="modal_footer">
              <button className="game-button green-button" style={{marginRight: 10}} onClick={confirmBuyExtraSpin} disabled={buySpinLoading}>
                {buySpinLoading ? "Purchasing..." : "Confirm"}
              </button>
              <button className="game-button red-button"
                onClick={() => setShowBuySpinModal(false)}
                disabled={buySpinLoading}
              >
                CANCEL
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showBuySpinError && (
        <div className="modal_box">
         <div className="modal_title">OOPS<button onClick={() => {
                  setShowBuySpinError(false);
                  setBuySpinErrorMessage("");
                }} className="modal_close"/></div>

          <motion.div
            className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          >

          <div className="modal_inside">
            <img
              src="/finger.png"
              alt="Error"
              style={{
                height: "80px",
                marginBottom: "10px",
                transform: "rotate(180deg)",
              }}
            />

            <div className="shadow2" style={{ fontSize: 28, fontWeight: 600, marginBottom: "10px" }}>
              Transaction Failed
            </div>

            <p className="info shadow1"
              style={{
                fontSize: "21px",
                color: "#ddd",
              }}
            >
              {buySpinErrorMessage}
            </p>

            </div>

            <div className="modal_footer">
              <button
              className="game-button red-button"
                onClick={() => {
                  setShowBuySpinError(false);
                  setBuySpinErrorMessage("");
                }}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showBuySpinSuccess && (
        <div className="modal_box">
        <div className="modal_title">Success<button className="modal_close" onClick={() => {
                  setShowBuySpinSuccess(false);
                }}/></div>
          <motion.div
            className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          >

          <div className="modal_inside">

            <img
              src="/finger.png"
              alt="Success"
              style={{ height: "80px", marginBottom: "10px" }}
            />

            <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: "10px" }}>
              Transaction Completed!
            </div>

            <p className="info"
              style={{
                fontSize: "18px",
              }}
            >
              <strong>+1 Spin</strong> Successfully Added!
            </p>

            </div>

            <div className="modal_footer">
              <button
              className="game-button red-button"
                onClick={() => {
                  setShowBuySpinSuccess(false);
                }}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}

     

      {/* Result modal */}
    <WinModal
  open={resultOpen}
  prize={resultPrize}
  onClose={() => setResultOpen(false)}
  onTryAgain={() => {
    setResultOpen(false);
    // optional: auto-spin again if you want:
    // handleSpinButton();
  }}
  onDouble={() => {
    // Hook your rewarded-ad flow here.
    // If you want to just close for now:
    setResultOpen(false);
  }}
/>

    </div>

  </div>
);

}
